import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCompleteAffiliateRegistrationMutation } from '../queries';
import { type AffiliateRegistrationFormValues, affiliateRegistrationSchema } from '../validations';
import { useAuthStore } from '@/stores/authStore';
import { handleApiError } from '@/lib/utils/apiErrorHandler';
import { getLocalTimezone } from '@/lib/helpers';
import { stanAnalytics } from '@/lib/stanAnalytics';
import type { AffiliateUser } from '@/lib/api/auth/types';

interface UseAffiliateRegistrationFormProps {
    /** Pre-filled affiliate user data from token */
    affiliateUser: AffiliateUser | undefined;
    /** JWT token for API authorization */
    token: string;
    /** Callback when registration succeeds */
    onSuccess: () => void;
}

/**
 * Hook to handle affiliate registration form logic.
 * Pre-fills form with data from token and handles registration API call.
 */
export function useAffiliateRegistrationForm({ affiliateUser, token, onSuccess }: UseAffiliateRegistrationFormProps) {
    const { mutateAsync: completeRegistration, isPending } = useCompleteAffiliateRegistrationMutation();
    const saveToken = useAuthStore(state => state.saveToken);

    const form = useForm<AffiliateRegistrationFormValues>({
        resolver: zodResolver(affiliateRegistrationSchema),
        defaultValues: {
            fullName: affiliateUser?.full_name ?? '',
            email: affiliateUser?.email ?? '',
            phoneNumber: affiliateUser?.phone_number ?? '',
            countryCode: '',
            password: '',
        },
        mode: 'onChange',
    });

    const onSubmit = form.handleSubmit(async data => {
        try {
            const payload = {
                full_name: data.fullName.trim() || affiliateUser?.username || '',
                username: affiliateUser?.username || '',
                email: data.email.trim(),
                password: data.password,
                phone_number: data.phoneNumber,
                country_code: data.countryCode,
                timezone: getLocalTimezone(),
            };

            const response = await completeRegistration({ payload, token });

            // Check for API-level errors
            if (response.status === 'error' && response.errors) {
                // Map API field errors to form fields
                const fieldMap: Record<string, keyof AffiliateRegistrationFormValues> = {
                    full_name: 'fullName',
                    email: 'email',
                    phone_number: 'phoneNumber',
                    password: 'password',
                };

                Object.entries(response.errors).forEach(([field, messages]) => {
                    const formField = fieldMap[field];
                    if (formField && messages[0]) {
                        form.setError(formField, { type: 'server', message: messages[0] });
                    }
                });
                return;
            }

            // Save token and track analytics
            if (response.token) {
                saveToken(response.token);

                stanAnalytics('signup', {
                    meta: { username: affiliateUser?.username || 'guest' },
                    props: {
                        email: data.email.trim(),
                        fullName: data.fullName.trim(),
                    },
                });

                onSuccess();
            }
        } catch (error: unknown) {
            const result = handleApiError(error, 'Registration Failed');

            // Map API field errors to form fields for validation errors
            if (result.type === 'validation' && result.fieldErrors) {
                const fieldMap: Record<string, keyof AffiliateRegistrationFormValues> = {
                    full_name: 'fullName',
                    email: 'email',
                    phone_number: 'phoneNumber',
                    password: 'password',
                };

                Object.entries(result.fieldErrors).forEach(([field, messages]) => {
                    const formField = fieldMap[field];
                    if (formField && messages[0]) {
                        form.setError(formField, { type: 'server', message: messages[0] });
                    }
                });
            }
        }
    });

    return {
        form,
        onSubmit,
        isSubmitting: isPending,
    };
}
